import React, { useContext } from 'react';
import proptypes from 'prop-types';
import { IconButton, Typography } from '@one-thd/sui-atomic-components';
import { ThumbsUp, ThumbsDown } from '@one-thd/sui-icons';
import {
  arrayOf,
  customType,
  number,
  params,
  shape,
  string as stringType,
  extend,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { PopoverFeedBackForm } from './FeedbackForm';
import { AddToContext } from '../../AddToContext';
import { getCurrentIntents, getSelectedMaterials } from '../../AddToHelpers';

const { string } = proptypes;

export const Feedback = ({ refType, refId }) => {
  const [likeStatus, setLikeStatus] = React.useState(null);
  const [showPopover, setShowPopover] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { groupedIntentsPreview, generatedIntents, mcvisId } = useContext(AddToContext);
  const { channel } = useContext(ExperienceContext);
  const { groupedIntents, sessionId } = groupedIntentsPreview || {};
  const FEEDBACK_TYPES = {
    LIKE: 'LIKE',
    DISLIKE: 'DISLIKE'
  };

  const [submitFeedback] = useLazyDataModel('interactWithAgent', {
    variables: {
      interactionType: 'FEEDBACK',
      metaData: {
        sessionId,
        channel,
        refType,
        refId,
        mcvis: mcvisId || '',
      }
    },
    context: {
      withAuth: true
    }
  });

  const sendFeedback = (feedback) => {
    const { type, category, comment } = feedback;
    const selectedMaterials = getSelectedMaterials(groupedIntents);
    const currentIntents = getCurrentIntents(generatedIntents, selectedMaterials);
    submitFeedback({
      variables: {
        feedbackData: {
          type,
          accept_discard: null,
          category,
          comment,
          currentIntents,
          selectionStrategy: null,
          listState: null
        }
      }
    });
  };

  const onLike = () => {
    if (likeStatus !== FEEDBACK_TYPES.LIKE) {
      setLikeStatus(FEEDBACK_TYPES.LIKE);
      sendFeedback({ type: FEEDBACK_TYPES.LIKE, category: null, comment: null });
    }
  };

  const onDislike = () => {
    setLikeStatus(FEEDBACK_TYPES.DISLIKE);
    setShowPopover(true);
  };

  const getColor = (status) => {
    if (likeStatus === null) return 'primary';
    return status === likeStatus ? 'brand' : 'subtle';
  };

  const handlePopoverClose = () => {
    setShowPopover(false);
  };

  const handlePopoverSubmit = (feedbackData) => {
    const { reason, comment } = feedbackData;
    sendFeedback({ type: FEEDBACK_TYPES.DISLIKE, category: reason, comment });
    handlePopoverClose();
  };

  return (
    <div className="sui-mt-2 sui-flex sui-flex-row sui-gap-2 sui-align-right sui-items-center sui-justify-end">
      <Typography variant="body-xs">
        Was this helpful?
      </Typography>
      <div className="sui-mt-1">
        <IconButton icon={ThumbsUp} aria-label="Helpful" color={getColor(FEEDBACK_TYPES.LIKE)} onClick={onLike} />
        <IconButton
          icon={ThumbsDown}
          aria-label="Not helpful"
          color={getColor(FEEDBACK_TYPES.DISLIKE)}
          onClick={onDislike}
          ref={(elem) => setAnchorEl(elem)}
        />
        {
          likeStatus === FEEDBACK_TYPES.DISLIKE && (
            <PopoverFeedBackForm
              open={showPopover}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              onSubmit={handlePopoverSubmit}
            />
          )
        }
      </div>
    </div>
  );
};

Feedback.displayName = 'Feedback';

Feedback.propTypes = {
  refType: string.isRequired,
  refId: string
};

Feedback.defaultProps = {
  refId: ''
};

Feedback.dataModel = extend({
  interactWithAgent: params({
    interactionType: customType('InteractionType').enum(['MESSAGE', 'FEEDBACK']).isRequired(),
    metaData: customType('MetadataInput').shape({
      refType: stringType(),
      refId: stringType(),
      sessionId: stringType(),
      channel: stringType(),
      mcvis: stringType()
    }),
    feedbackData: customType('FeedbackInput').shape({
      type: customType('FeedbackType').enum(['LIKE', 'DISLIKE', 'NONE']),
      accept_discard: customType('AcceptDiscardType').enum(['ACCEPT', 'DISCARD', 'NONE']),
      category: stringType(),
      comment: stringType(),
      currentIntents: arrayOf(customType('IntentGroup').shape({
        groupName: stringType(),
        materials: arrayOf(customType('IntentItem').shape({
          intent: stringType(),
          quantity: number(),
          change: customType('IntentChangeType').shape({
            type: number(),
            previous: customType('IntentPreviousItem').shape({
              intent: stringType(),
              quantity: number()
            }),
          })
        })),
        change: customType('ChangeType').shape({
          type: number(),
          previous: stringType()
        })
      })),
      selectionStrategy: customType('SelectionStrategy').enum(['MANUAL_SELECTION', 'AUTO_SELECTION']),
      listState: customType('ListState').enum(['NEW', 'EXISTING'])
    })
  }).mutation().shape({
    metadata: shape({
      statusMessage: stringType()
    }),
    error: stringType(),
    materialsGroups: arrayOf(
      shape({
        groupName: stringType(),
        materials: arrayOf(
          shape({
            intent: stringType(),
            quantity: number()
          })
        )
      })
    )
  })
});
